import { combineReducers, configureStore, Middleware } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import authReducer from './reducers/authReducer';
import lotteryReducer from './reducers/lotteryReducer';
import drawingReducer from './reducers/drawingReducer';
import drawingBaseReducer from './reducers/drawingBaseReducer';
import prizeReducer from './reducers/prizeReducer';
import userReducer from './reducers/userReducer';
import workflowReducer from './reducers/workflowReducer';
import alertReducer, { addAlert } from './reducers/alertReducer';
import lockReducer from './reducers/lockReducer';
import { lockAction, unlockAction } from './actions/lockActions';
import { currentUser, login, logout } from './actions/authActions';

const rootReducer = combineReducers({
  auth: authReducer,
  lotteries: lotteryReducer,
  drawings: drawingReducer,
  drawingBases: drawingBaseReducer,
  prizes: prizeReducer,
  users: userReducer,
  workflow: workflowReducer,
  alerts: alertReducer,
  lock: lockReducer,
});

const persistedReducer = persistReducer(
  {
    key: 'ps-sparen',
    storage: storageSession,
    whitelist: ['auth'],
  },
  rootReducer
);

let pendingCount = 0;
export const lockMiddleware: Middleware = (storeApi) => (next) => (action) => {
  const type = (action as { type: string }).type;

  if (type.endsWith('/pending')) {
    if (pendingCount === 0) {
      storeApi.dispatch(lockAction());
    }
    pendingCount += 1;
  }

  if (type.endsWith('/fulfilled') || type.endsWith('/rejected')) {
    pendingCount = Math.max(0, pendingCount - 1);
    if (pendingCount === 0) {
      storeApi.dispatch(unlockAction());
    }
  }

  return next(action);
};

let sessionExpiredHandled = false;

const resetSessionFlag = () => {
  if (pendingCount === 0) {
    sessionExpiredHandled = false;
  } else {
    window.setTimeout(resetSessionFlag, 100);
  }
};

export const messageMiddleware: Middleware = () => (next) => (action) => {
  const type = (action as { type: string }).type;
  if (type.endsWith('/fulfilled')) {
    const payload = (action as { payload?: { showMessage?: string } }).payload;
    if (payload?.showMessage) {
      store.dispatch(addAlert({ message: payload.showMessage, severity: 'success' }));
    }
  }
  return next(action);
};

const isSessionExpired = (type: string, payload?: { redirected?: boolean; status?: number }) =>
  payload?.redirected || (payload?.status === 401 && type !== login.rejected.type);

export const errorMiddleware: Middleware = () => (next) => (action) => {
  const type = (action as { type: string }).type;
  if (type.endsWith('/rejected') && type !== currentUser.rejected.type && type !== logout.rejected.type) {
    const payload = (action as { payload?: { redirected?: boolean; status?: number; message?: string } }).payload;

    if (isSessionExpired(type, payload)) {
      if (!sessionExpiredHandled) {
        sessionExpiredHandled = true;

        Promise.resolve().then(() => {
          store.dispatch(
            addAlert({
              message: 'Sitzung abgelaufen. Bitte melden Sie sich erneut an.',
              severity: 'warning',
            })
          );
          store.dispatch(logout());
        });

        window.setTimeout(resetSessionFlag, 100);
      }
      return next(action);
    }

    store.dispatch(addAlert({ message: payload?.message || 'Ein Fehler ist aufgetreten', severity: 'warning' }));
  }

  return next(action);
};

export const sessionCheckMiddleware: Middleware = (storeApi) => (next) => async (action) => {
  const result = next(action);

  if (typeof action === 'object' && action !== null && 'type' in action) {
    if (action.type === REHYDRATE) {
      try {
        const res = await store.dispatch(currentUser());
        if (res.payload && typeof res.payload === 'object' && 'redirected' in res.payload && (res.payload as { redirected?: boolean }).redirected) {
          if (storeApi.getState().auth.isAuthenticated) {
            store.dispatch(logout());
          }
        }
      } catch {
        if (storeApi.getState().auth.isAuthenticated) {
          store.dispatch(logout());
        }
      }
    }
  }

  return result;
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(lockMiddleware)
      .concat(sessionCheckMiddleware)
      .concat(messageMiddleware)
      .concat(errorMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const persistor = persistStore(store);
